import { toast } from "sonner";
import { CheckCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { OPS, fmtDT } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { CrudPage } from "@/components/CrudPage";
import { StatusBadge, Badge } from "@/components/Badges";

const SLOTS = ["06:00-14:00", "07:00-15:00", "12:00-20:00", "14:00-22:00", "15:00-23:00", "22:00-06:00", "23:00-07:00", "Lido 06:00-14:00", "Lido 14:00-22:00", "Lido 22:00-06:00"];

const JOURNAL_FIELDS = [
  { key: "hotel_id", label: "Struttura", type: "hotel", required: true },
  { key: "slot", label: "Turno", type: "select", options: SLOTS, required: true },
  { key: "type", label: "Tipo", type: "select", options: ["Evento", "Nota", "Allarme", "Comunicazione"], required: true },
  { key: "text", label: "Descrizione", type: "textarea", required: true },
];
const JOURNAL_COLS = [
  { key: "created_at", label: "Data/ora", render: (r) => <span className="font-mono text-xs">{fmtDT(r.created_at)}</span> },
  { key: "slot", label: "Turno" },
  { key: "type", label: "Tipo", render: (r) => <Badge tone={r.type === "Allarme" ? "bad" : r.type === "Evento" ? "warn" : "neutral"}>{r.type}</Badge> },
  { key: "text", label: "Descrizione" },
  { key: "created_by", label: "Autore" },
];

const HANDOVER_FIELDS = [
  { key: "hotel_id", label: "Struttura", type: "hotel", required: true },
  { key: "slot", label: "Turno cedente", type: "select", options: SLOTS, required: true },
  { key: "from_name", label: "Operatore cedente", required: true },
  { key: "to_name", label: "Operatore subentrante", required: true },
  { key: "notes", label: "Consegne", type: "textarea", required: true },
  { key: "open_items", label: "Pendenze aperte", type: "textarea" },
];
const HANDOVER_COLS = [
  { key: "created_at", label: "Data/ora", render: (r) => <span className="font-mono text-xs">{fmtDT(r.created_at)}</span> },
  { key: "slot", label: "Turno" },
  { key: "from_name", label: "Da → A", render: (r) => `${r.from_name} → ${r.to_name}` },
  { key: "notes", label: "Consegne" },
  { key: "open_items", label: "Pendenze" },
  { key: "acknowledged", label: "Presa visione", render: (r) => <StatusBadge value={r.acknowledged || "no"} /> },
];

export default function Giornale() {
  const { user } = useAuth();
  const ack = async (r, reload) => {
    try { await api.put(`/handovers/${r.id}`, { acknowledged: "si", ack_by: user.name }); toast.success("Presa visione confermata"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <PageHeader eyebrow="CRM di commessa" title="Giornale di servizio & Consegne" subtitle="Registrazione degli eventi di turno e passaggio di consegne tracciato tra operatori." />
      <Tabs defaultValue="giornale">
        <TabsList>
          <TabsTrigger value="giornale" data-testid="tab-journal">Giornale di servizio</TabsTrigger>
          <TabsTrigger value="consegne" data-testid="tab-handovers">Passaggio consegne</TabsTrigger>
        </TabsList>
        <TabsContent value="giornale" className="mt-4">
          <CrudPage resource="journal" title="Voce di giornale" testid="journal" fields={JOURNAL_FIELDS} columns={JOURNAL_COLS} writeRoles={OPS} newLabel="Nuova voce" params={{ limit: 300 }} />
        </TabsContent>
        <TabsContent value="consegne" className="mt-4">
          <CrudPage resource="handovers" title="Passaggio consegne" testid="handovers" fields={HANDOVER_FIELDS} columns={HANDOVER_COLS} writeRoles={OPS}
            newLabel="Nuova consegna" params={{ limit: 300 }} defaults={{ acknowledged: "no" }}
            extraActions={(r, reload) => OPS.includes(user.role) && r.acknowledged !== "si" && (
              <Button size="sm" variant="outline" className="gap-1 mr-1" onClick={() => ack(r, reload)} data-testid="handover-ack-btn"><CheckCheck className="w-3.5 h-3.5" />Conferma</Button>
            )} />
        </TabsContent>
      </Tabs>
    </>
  );
}
